import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getSnakeDraftView, snakeDraftPick } from '../../data';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
    Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, makeStyles, Box,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const POSITION_COLORS = {
    'Batter': '#2196f3',
    'Bowler': '#4caf50',
    'All-Rounder': '#ff9800',
    'WK-Batter': '#9c27b0',
};

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 1100,
        margin: '0 auto',
        padding: theme.spacing(2),
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    nextPickBanner: {
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.type === 'dark' ? '#1e3a5f' : '#e3f2fd',
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    draftBoard: {
        marginBottom: theme.spacing(3),
        overflowX: 'auto',
    },
    roundRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    directionIcon: {
        color: theme.palette.text.secondary,
        fontSize: 18,
    },
    pickCell: {
        minWidth: 120,
        padding: theme.spacing(0.5, 1),
        borderRadius: 4,
        textAlign: 'center',
        fontSize: '0.75rem',
    },
    pickCellFilled: {
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#f0f0f0',
    },
    pickCellCurrent: {
        backgroundColor: theme.palette.type === 'dark' ? '#1e3a5f' : '#bbdefb',
        border: `2px solid ${theme.palette.primary.main}`,
    },
    pickCellEmpty: {
        backgroundColor: 'transparent',
        border: `1px dashed ${theme.palette.divider}`,
    },
    squadSection: {
        marginTop: theme.spacing(2),
    },
    playerSearch: {
        marginBottom: theme.spacing(1),
    },
    availableTable: {
        maxHeight: 300,
        overflow: 'auto',
    },
    posChip: {
        color: '#fff',
        fontSize: '0.7rem',
        height: 20,
    },
    progressInfo: {
        color: theme.palette.text.secondary,
    },
}));

function SnakeDraftView() {
    const classes = useStyles();
    const { leagueID } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pickDialogOpen, setPickDialogOpen] = useState(false);
    const [playerSearch, setPlayerSearch] = useState('');
    const [selectedPlayerID, setSelectedPlayerID] = useState('');
    const [pickError, setPickError] = useState(null);
    const [picking, setPicking] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await getSnakeDraftView(leagueID);
            setData(result);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [leagueID]);

    const handlePickSubmit = async () => {
        if (!data || !data.nextPick || !selectedPlayerID) return;
        try {
            setPicking(true);
            setPickError(null);
            await snakeDraftPick(leagueID, data.nextPick.squadName, selectedPlayerID);
            setPickDialogOpen(false);
            setSelectedPlayerID('');
            setPlayerSearch('');
            await loadData();
        } catch (err) {
            setPickError(err.message);
        } finally {
            setPicking(false);
        }
    };

    const canPick = data && data.nextPick && (
        data.isAdmin || data.userSquad === data.nextPick.squadName
    );

    const filteredPlayers = data && playerSearch.length >= 2
        ? (data.availablePlayers || []).filter(p =>
            p.name.toLowerCase().includes(playerSearch.toLowerCase()) ||
            p.team.toLowerCase().includes(playerSearch.toLowerCase())
        ).slice(0, 30)
        : [];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <CircularProgress aria-label="Loading snake draft" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <Typography color="error">{error}</Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h6">{data.leagueName} - Snake Draft</Typography>
                <div>
                    <Link to={`/leagues/${leagueID}`}>
                        <Button variant="outlined" size="small">Back to League</Button>
                    </Link>
                </div>
            </div>

            {/* Progress */}
            <Typography variant="body2" className={classes.progressInfo}>
                {data.picksMade} of {data.totalPicks} picks made ({data.totalRounds} rounds)
            </Typography>

            {/* Next Pick Banner */}
            {data.nextPick && (
                <div className={classes.nextPickBanner} data-testid="next-pick-banner">
                    <Typography variant="subtitle1">
                        Next pick: <strong>{data.nextPick.squadName}</strong>
                        {' '} (Round {data.nextPick.round}, Pick #{data.nextPick.overallPick})
                    </Typography>
                    {canPick && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => setPickDialogOpen(true)}
                            data-testid="make-pick-button"
                        >
                            Make Pick
                        </Button>
                    )}
                </div>
            )}

            {!data.nextPick && (
                <div className={classes.nextPickBanner}>
                    <Typography variant="subtitle1">Draft Complete</Typography>
                </div>
            )}

            {/* Draft Board */}
            {data.draftOrder.length > 0 && (
                <div className={classes.draftBoard}>
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="Draft board">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Round</TableCell>
                                    <TableCell />
                                    {data.draftOrder.map(name => (
                                        <TableCell key={name} align="center">
                                            <Typography variant="caption" style={{ fontWeight: 600 }}>
                                                {name}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.draftBoard.map(round => (
                                    <TableRow key={round.round}>
                                        <TableCell>
                                            <Typography variant="body2">R{round.round}</Typography>
                                        </TableCell>
                                        <TableCell style={{ width: 30 }}>
                                            {round.direction === 'left-to-right'
                                                ? <ArrowForwardIcon className={classes.directionIcon} aria-label="Left to right" />
                                                : <ArrowBackIcon className={classes.directionIcon} aria-label="Right to left" />
                                            }
                                        </TableCell>
                                        {data.draftOrder.map((squadName) => {
                                            const pick = round.picks.find(p => p.squadName === squadName);
                                            const isCurrent = data.nextPick &&
                                                data.nextPick.round === round.round &&
                                                data.nextPick.squadName === squadName;
                                            let cellClass = classes.pickCellEmpty;
                                            if (pick && pick.playerName) {
                                                cellClass = classes.pickCellFilled;
                                            } else if (isCurrent) {
                                                cellClass = classes.pickCellCurrent;
                                            }
                                            return (
                                                <TableCell key={`${round.round}-${squadName}`} align="center">
                                                    <div className={`${classes.pickCell} ${cellClass}`}>
                                                        {pick && pick.playerName ? (
                                                            <>
                                                                <div style={{ fontWeight: 500 }}>{pick.playerName}</div>
                                                                <Chip
                                                                    label={pick.playerPos}
                                                                    size="small"
                                                                    className={classes.posChip}
                                                                    style={{ backgroundColor: POSITION_COLORS[pick.playerPos] || '#757575' }}
                                                                />
                                                            </>
                                                        ) : isCurrent ? (
                                                            <Typography variant="caption" color="primary">
                                                                On the clock
                                                            </Typography>
                                                        ) : null}
                                                    </div>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            {/* Squad Summaries */}
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 16 }}>
                Squads
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                {data.squads.map(squad => (
                    <Paper key={squad.name} style={{ padding: 12, minWidth: 200 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            {squad.name} ({squad.squadSize}/{squad.squadMaxSize})
                        </Typography>
                        {squad.players.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                <Typography variant="caption">{p.name}</Typography>
                                <Chip
                                    label={p.pos}
                                    size="small"
                                    className={classes.posChip}
                                    style={{ backgroundColor: POSITION_COLORS[p.pos] || '#757575' }}
                                />
                            </div>
                        ))}
                        {squad.players.length === 0 && (
                            <Typography variant="caption" color="textSecondary">No picks yet</Typography>
                        )}
                    </Paper>
                ))}
            </div>

            {/* Pick Dialog */}
            <Dialog open={pickDialogOpen} onClose={() => setPickDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Make Pick for {data.nextPick?.squadName}
                    {data.nextPick && (
                        <Typography variant="body2" color="textSecondary">
                            Round {data.nextPick.round}, Pick #{data.nextPick.overallPick}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Search Player (name or team)"
                        value={playerSearch}
                        onChange={e => setPlayerSearch(e.target.value)}
                        className={classes.playerSearch}
                        autoFocus
                        data-testid="player-search-input"
                    />
                    {filteredPlayers.length > 0 && (
                        <Paper className={classes.availableTable}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Team</TableCell>
                                        <TableCell>Pos</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPlayers.map(p => (
                                        <TableRow
                                            key={p.id}
                                            hover
                                            selected={selectedPlayerID === p.id}
                                            onClick={() => { setSelectedPlayerID(p.id); setPlayerSearch(p.name); }}
                                            style={{ cursor: 'pointer' }}
                                            data-testid={`player-row-${p.id}`}
                                        >
                                            <TableCell>{p.name}</TableCell>
                                            <TableCell>{p.team}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={p.pos}
                                                    size="small"
                                                    className={classes.posChip}
                                                    style={{ backgroundColor: POSITION_COLORS[p.pos] || '#757575' }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                    {selectedPlayerID && (
                        <Typography variant="body2" style={{ marginTop: 8 }}>
                            Selected: <strong>{(data.availablePlayers || []).find(p => p.id === selectedPlayerID)?.name || selectedPlayerID}</strong>
                        </Typography>
                    )}
                    {pickError && (
                        <Typography color="error" style={{ marginTop: 8 }}>{pickError}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPickDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handlePickSubmit}
                        color="primary"
                        variant="contained"
                        disabled={!selectedPlayerID || picking}
                        data-testid="confirm-pick-button"
                    >
                        {picking ? 'Picking...' : 'Confirm Pick'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default SnakeDraftView;
